import { useEffect, useMemo, useCallback, useState } from 'react';
import axios from 'axios';
import { useProjectFlowStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores/projectFlowStore';
import { useAppSelector, useMainTimePeriod, useSnackbar, useLocation } from 'src/utils/Hooks/typedHooks';
import { getQueryParamValue } from 'src/utils/app';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { buildTabAndSubtabLink } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { BEACON_TABS, BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { useContentGenReviewStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { extractSectionData, highlightKeywordsInText } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/utils';
import { useGetSingleProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import {
  useUpdateContentLibrary,
  useExportContent,
  useGetContentLibrary
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useContentGenerate';
import { useComparisonMetrics } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useComparisonMetrics';
import { useQuery } from '@tanstack/react-query';
import {
  apiCompareProductKeywords,
  apiGetContentGenerationStatus,
  apiGetCurrentContent
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/contentGenApis';
import { parseAndMergeNewContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/utils';
import {
  ContentMetricType,
  ContentLibraryManagementRequest,
  KeywordSection,
  ContentLibraryRecord,
  MasterContentRecord,
  ProductKeywordsComparisonRequest,
  ProductKeywordsPayload
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import type { ContentGenerationItem } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { ActionBarProps } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/LibraryReviewActionBar';
import {
  buildKeywordsPayload,
  formatDateForDisplay,
  formatKeywordSections
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/reviewUtils';

export const useProjectReview = () => {
  const { showSnackbar } = useSnackbar();
  const app = useAppSelector((state) => state.app);
  const location = useLocation();
  const retailerId = getQueryParamValue('rid', '');
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const { endWeek } = useMainTimePeriod();
  const { beaconNavigator } = useBeaconNavigator();
  const users = useAppSelector((state) => state.user.team);

  const hasDeepLinkParams = Boolean(getQueryParamValue('sku') || getQueryParamValue('contentGenId'));

  // Get projectId from project flow store
  const projectId = useProjectFlowStore((state) => state.projectId);

  const {
    getCurrentContentGenId,
    getCurrentContentGenItemIndex,
    goToNextContentGenItem,
    goToPreviousContentGenItem,
    hasContentGenItems,
    contentGenItemsToReview,
    clearContentGenItems
  } = useContentGenReviewStore();

  // Align current item with URL params if provided (supports deep-linking to a SKU)
  useEffect(() => {
    const skuFromUrl = getQueryParamValue('sku');
    const idFromUrl = getQueryParamValue('contentGenId');
    if (!skuFromUrl && !idFromUrl) return;

    const state = useContentGenReviewStore.getState();
    const items = state.contentGenItemsToReview;
    const currentIdx = state.getCurrentContentGenItemIndex();

    const idxById = idFromUrl ? items.findIndex((i) => i.skuContentGenerationId === idFromUrl) : -1;
    const idxBySku = skuFromUrl ? items.findIndex((i) => i.stacklineSku === skuFromUrl) : -1;
    const targetIdx = idxById >= 0 ? idxById : idxBySku;

    if (targetIdx >= 0 && targetIdx !== currentIdx) {
      state.setCurrentContentGenItemIndex(targetIdx);
    }
  }, []);

  // Redirect to Generate when no items and no deep link params
  useEffect(() => {
    if (!hasContentGenItems() && !hasDeepLinkParams) {
      const generationsRoute = buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.GENERATION,
        subtab: BEACON_SUBTABS.GENERATE
      });
      beaconNavigator.go(generationsRoute);
    }
  }, [hasContentGenItems, hasDeepLinkParams, app, location, beaconNavigator]);

  const currentContentGenId = getCurrentContentGenId();
  const currentIndex = getCurrentContentGenItemIndex();
  const totalReviews = contentGenItemsToReview.length;
  
  // Get current item from store
  const currentItem = contentGenItemsToReview[currentIndex];

  // Inputs prefer current item; fall back to deep link params for isolated usage
  const retailerSkuInput = currentItem?.retailerSku || getQueryParamValue('sku') || '';
  const profileIdInput = currentItem?.profileId || getQueryParamValue('profileId') || undefined;
  const contentGenIdInput = currentItem?.skuContentGenerationId;

  // Resolve left (current) and right (generated) and compare, mirroring useLibraryReview
  const stacklineSku = currentItem?.stacklineSku || retailerSkuInput;

  // State for selected versions (aligned with useLibraryReview)
  const [leftSelectedVersion, setLeftSelectedVersion] = useState<string>('current');
  const [rightSelectedVersion, setRightSelectedVersion] = useState<string>(contentGenIdInput || 'current');

  // Update rightSelectedVersion when contentGenIdInput changes
  useEffect(() => {
    if (contentGenIdInput) {
      setRightSelectedVersion(contentGenIdInput);
    }
  }, [contentGenIdInput]);

  // Fetch content library to get all versions - filtered by projectId
  const { data: libraryData, isLoading: isLoadingLibrary } = useGetContentLibrary({
    retailerId: Number(retailerId),
    retailerSkus: [retailerSkuInput],
    projectId: projectId || null
  });

  // Get the library record for this SKU
  const libraryRecord = useMemo<ContentLibraryRecord | null>(() => {
    if (!libraryData?.records || libraryData.records.length === 0) return null;
    return libraryData.records.find((record) => record.retailerSku === retailerSkuInput) || libraryData.records[0];
  }, [libraryData, retailerSkuInput]);

  // Uploaded content: API may return uploadedContent or masterContent (support both)
  const uploadedOrMasterContent = useMemo<MasterContentRecord | undefined>(() => {
    if (!libraryRecord) return undefined;
    return libraryRecord.uploadedContent ?? libraryRecord.masterContent;
  }, [libraryRecord]);

  // Collect userIds referenced by versions for fetching user profiles
  const versionUserIds = useMemo(() => {
    const ids = new Set<string>();
    if (libraryRecord?.versions && libraryRecord.versions.length > 0) {
      libraryRecord.versions.forEach((version) => {
        ids.add(version.createdBy);
      });
    }
    return Array.from(ids);
  }, [libraryRecord]);

  // Fetch user profile metadata for display names
  const { data: userMetadata } = useQuery({
    queryKey: ['userMetadata', versionUserIds],
    queryFn: async () => {
      if (!versionUserIds || versionUserIds.length === 0) return {};
      const response = await axios.post('/api/user/GetUserProfilesFromUserIds', versionUserIds);
      return response.data as Record<
        string,
        { firstName?: string; lastName?: string; email?: string; role?: string }
      >;
    },
    enabled: (versionUserIds?.length || 0) > 0,
    staleTime: 5 * 60 * 1000
  });

  // Build version list for dropdowns (already filtered by projectId from API)
  const allVersionOptions = useMemo(() => {
    const options: Array<{
      id: string;
      label: string;
      createdBy: {
        userId: string;
        displayName: string;
        role: string;
        imageSrc: string;
      };
      updatedAt?: string;
    }> = [];

    // Add versions (already filtered by projectId from API)
    if (libraryRecord?.versions && libraryRecord.versions.length > 0) {
      libraryRecord.versions.forEach((version, index) => {
        const versionId = version.skuContentGenerationId;
        const resolvedUserId = version.createdBy || version.updatedBy || '';
        const meta = userMetadata?.[resolvedUserId];
        const metaDisplayName =
          resolvedUserId === 'content-generation-workflow'
            ? 'Stackline'
            : meta?.firstName && meta?.lastName
            ? `${meta.firstName} ${meta.lastName}`
            : undefined;
        const role = (meta?.role as string | undefined) || (users?.find((u: { id: string }) => u.id === resolvedUserId)?.role as string | undefined);
        options.push({
          id: versionId,
          label: `Version ${libraryRecord.versions.length - index}`,
          createdBy: {
            userId: resolvedUserId || 'content-generation-workflow',
            displayName: metaDisplayName || 'Stackline',
            role: role || 'support',
            imageSrc: ''
          },
          updatedAt: version.updatedAt
        });
      });
    }

    // Add uploaded (master) content second to last
    if (uploadedOrMasterContent) {
      const masterUpdatedAt = uploadedOrMasterContent.updatedAt ||
        (uploadedOrMasterContent.timestamp ? new Date(uploadedOrMasterContent.timestamp).toISOString() : undefined);
      options.push({
        id: 'master',
        label: 'Uploaded Content',
        createdBy: {
          userId: 'content-generation-workflow',
          displayName: 'Stackline',
          role: 'support',
          imageSrc: ''
        },
        updatedAt: masterUpdatedAt
      });
    }

    // Add current last
    const currentUpdatedAt = libraryRecord?.currentContent?.updatedAt || 
      (libraryRecord?.currentContent?.timestamp ? new Date(libraryRecord.currentContent.timestamp).toISOString() : undefined);
    options.push({
      id: 'current',
      label: 'Current Content',
      createdBy: {
        userId: 'content-generation-workflow',
        displayName: 'Stackline',
        role: 'support',
        imageSrc: ''
      },
      updatedAt: currentUpdatedAt
    });

    return options;
  }, [libraryRecord, users, userMetadata, uploadedOrMasterContent]);

  const leftComparisonOptions = allVersionOptions;
  const availableVersions = allVersionOptions;

  const buildPayload = useCallback(
    (params: {
      title?: string;
      description?: string;
      bullets?: string[];
      skuContentGenerationId?: string | undefined;
    }): ProductKeywordsPayload => {
      return buildKeywordsPayload(
        { retailerId: Number(retailerId), retailerSku: retailerSkuInput, weekId: endWeek },
        {
          title: params.title,
          description: params.description,
          bullets: params.bullets,
          skuContentGenerationId: params.skuContentGenerationId
        }
      );
    },
    [retailerId, retailerSkuInput, endWeek]
  );

  type LeftResolved = { title?: string; description?: string; bullets?: string[]; itemHighlights?: string; timestamp?: number; skuId?: string };
  type RightResolved = { title?: string; description?: string; bullets?: string[]; itemHighlights?: string; timestamp?: number; skuId?: string } | undefined;
  type GeneratedStatusRecord = ContentGenerationItem & { libraryStatus?: string };
  interface ProjectCompareSections {
    left: KeywordSection[];
    right: KeywordSection[];
    leftResolved: LeftResolved;
    rightResolved?: RightResolved;
    rightRaw?: GeneratedStatusRecord;
  }

  // Memoize the query key - now includes selected versions
  const projectReviewQueryKey = useMemo(
    () => ['PROJECT_REVIEW_COMPARE', retailerSkuInput, retailerId, endWeek, leftSelectedVersion, rightSelectedVersion] as const,
    [retailerSkuInput, retailerId, endWeek, leftSelectedVersion, rightSelectedVersion]
  );

  const {
    data: comparisonSections,
    isLoading: isLoadingCompare,
    error
  } = useQuery<ProjectCompareSections>({
    queryKey: projectReviewQueryKey,
    queryFn: async () => {
      // Helper to resolve selection into content and sku id (mirroring useLibraryReview)
      const resolveSelection = async (
        selectionId: string
      ): Promise<{
        title?: string;
        description?: string;
        bullets?: string[];
        itemHighlights?: string;
        skuId?: string | undefined;
        timestamp?: number;
      }> => {
        if (selectionId === 'current') {
          const res = await apiGetCurrentContent({
            retailerSku: retailerSkuInput,
            retailerId: Number(retailerId),
            weekId: endWeek,
            skuContentGenerationId: ''
          });
          return {
            title: res.data?.title,
            description: res.data?.description,
            bullets: res.data?.bullets,
            itemHighlights: res.data?.itemHighlights,
            skuId: undefined,
            timestamp: res.data?.timestamp
          };
        }

        if (selectionId === 'master') {
          if (!uploadedOrMasterContent) throw new Error('Uploaded content not available');
          return {
            title: uploadedOrMasterContent.title,
            description: uploadedOrMasterContent.description,
            bullets: uploadedOrMasterContent.bullets || [],
            skuId: undefined
          };
        }

        // Generated version
        const statusRes = await apiGetContentGenerationStatus({
          beaconClientId: beaconClientId || 0,
          skuContentGenerationIds: [selectionId]
        });
        const item = statusRes.data?.[selectionId];
        if (!item) throw new Error('Generated content not found');
        const parsed = parseAndMergeNewContent(item);
        return {
          title: parsed.content?.title,
          description: parsed.content?.description,
          bullets: parsed.content?.featureBullets || [],
          itemHighlights: parsed.content?.itemHighlights,
          skuId: selectionId
        };
      };

      const [leftResolved, rightResolved] = await Promise.all([
        resolveSelection(leftSelectedVersion),
        resolveSelection(rightSelectedVersion)
      ]);

      const currentPayload: ProductKeywordsPayload = buildPayload({
        title: leftResolved.title,
        description: leftResolved.description,
        bullets: leftResolved.bullets,
        skuContentGenerationId: leftResolved.skuId
      });

      const newPayload: ProductKeywordsPayload = buildPayload({
        title: rightResolved.title,
        description: rightResolved.description,
        bullets: rightResolved.bullets,
        skuContentGenerationId: rightResolved.skuId
      });

      const body: ProductKeywordsComparisonRequest = {
        current: currentPayload,
        new: newPayload
      };

      const compareRes = await apiCompareProductKeywords(body);
      const currentSections = compareRes.data?.data?.current_analysis?.sections as KeywordSection[] | undefined;
      const newSections = compareRes.data?.data?.new_analysis?.sections as KeywordSection[] | undefined;

      // Get rightRaw for version details
      let rightRaw: GeneratedStatusRecord | undefined;
      if (rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master') {
        const statusRes = await apiGetContentGenerationStatus({
          beaconClientId: beaconClientId || 0,
          skuContentGenerationIds: [rightSelectedVersion]
        });
        rightRaw = statusRes.data?.[rightSelectedVersion];
      }

      return {
        left: formatKeywordSections(currentSections),
        leftResolved: leftResolved,
        right: formatKeywordSections(newSections),
        rightResolved: rightResolved,
        rightRaw
      };
    },
    enabled:
      !!retailerSkuInput &&
      !!retailerId &&
      !!endWeek &&
      !!leftSelectedVersion &&
      !!rightSelectedVersion &&
      !!(leftSelectedVersion !== 'master' || uploadedOrMasterContent) &&
      !!(rightSelectedVersion !== 'master' || uploadedOrMasterContent) &&
      (leftSelectedVersion === 'current' || leftSelectedVersion === 'master' || !!beaconClientId) &&
      (rightSelectedVersion === 'current' || rightSelectedVersion === 'master' || !!beaconClientId),
    staleTime: 0, // Allow immediate refetch when invalidated
    refetchOnWindowFocus: false
  });

  const leftSections = useMemo<KeywordSection[]>(() => (comparisonSections?.left as KeywordSection[]) || [], [comparisonSections]);
  const rightSections = useMemo<KeywordSection[]>(() => (comparisonSections?.right as KeywordSection[]) || [], [comparisonSections]);
  const isLoadingLeft = isLoadingCompare || isLoadingLibrary;
  const isLoadingRight = isLoadingCompare || isLoadingLibrary;
  const isLoading = isLoadingCompare || isLoadingLibrary;

  const currentContentPhrasesKeywords = useMemo(() => {
    const sections = leftSections
      ? extractSectionData({ weightedKeywordPhrases: { sections: leftSections } })
      : {};
    const itemHighlights = comparisonSections?.leftResolved?.itemHighlights;
    if (itemHighlights) {
      sections['Item Highlights'] = {
        phrases: highlightKeywordsInText(itemHighlights, leftSections),
        keywords: []
      };
    }
    return sections;
  }, [leftSections, comparisonSections?.leftResolved?.itemHighlights]);

  const generatedContentPhrasesKeywords = useMemo(() => {
    const sections = rightSections
      ? extractSectionData({ weightedKeywordPhrases: { sections: rightSections } })
      : {};
    const itemHighlights = comparisonSections?.rightResolved?.itemHighlights;
    if (itemHighlights) {
      sections['Item Highlights'] = {
        phrases: highlightKeywordsInText(itemHighlights, rightSections),
        keywords: []
      };
    }
    return sections;
  }, [rightSections, comparisonSections?.rightResolved?.itemHighlights]);

  // Fetch raw content generation item to get profileVersion
  const { data: contentGenItemData } = useQuery({
    queryKey: ['ContentGenerationItem', rightSelectedVersion],
    queryFn: async () => {
      if (!beaconClientId || !rightSelectedVersion || rightSelectedVersion === 'current' || rightSelectedVersion === 'master') return null;
      const response = await apiGetContentGenerationStatus({
        beaconClientId,
        skuContentGenerationIds: [rightSelectedVersion]
      });
      return response.data[rightSelectedVersion];
    },
    enabled: !!beaconClientId && !!rightSelectedVersion && rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master'
  });

  const profileVersion = contentGenItemData?.profileVersion;

  // Profile for details section
  const { profile } = useGetSingleProfile(profileIdInput);

  // Metrics logic via shared hook
  const canFetchMetrics = !isLoadingLeft && !isLoadingRight;
  const canFetchNewMetrics = canFetchMetrics && !!comparisonSections?.rightResolved;
  const { leftMetrics, rightMetrics, metricLoadingStates: pairLoading } = useComparisonMetrics({
    retailerSku: retailerSkuInput,
    retailerId: Number(retailerId),
    weekId: endWeek,
    stacklineSku,
    profileId: profileIdInput,
    profileVersion,
    leftResolved: {
      title: comparisonSections?.leftResolved?.title,
      description: comparisonSections?.leftResolved?.description,
      bullets: comparisonSections?.leftResolved?.bullets,
      itemHighlights: comparisonSections?.leftResolved?.itemHighlights
    },
    rightResolved: comparisonSections?.rightResolved
      ? {
          title: comparisonSections?.rightResolved?.title,
          description: comparisonSections?.rightResolved?.description,
          bullets: comparisonSections?.rightResolved?.bullets,
          itemHighlights: comparisonSections?.rightResolved?.itemHighlights
        }
      : undefined,
    leftSections,
    rightSections,
    enabledLeft: canFetchMetrics,
    enabledRight: canFetchNewMetrics
  });

  const metricLoadingStates = {
    currentUtilization: pairLoading.leftUtilization,
    currentKeywordUsage: pairLoading.leftKeywordUsage,
    currentKeywordProductivity: pairLoading.leftKeywordProductivity,
    currentKeywordPopularity: pairLoading.leftKeywordPopularity,
    newUtilization: pairLoading.rightUtilization,
    newKeywordUsage: pairLoading.rightKeywordUsage,
    newKeywordProductivity: pairLoading.rightKeywordProductivity,
    newKeywordPopularity: pairLoading.rightKeywordPopularity,
    // Also expose as left/right for LibraryReview-style usage
    leftUtilization: pairLoading.leftUtilization,
    leftKeywordUsage: pairLoading.leftKeywordUsage,
    leftKeywordProductivity: pairLoading.leftKeywordProductivity,
    leftKeywordPopularity: pairLoading.leftKeywordPopularity,
    rightUtilization: pairLoading.rightUtilization,
    rightKeywordUsage: pairLoading.rightKeywordUsage,
    rightKeywordProductivity: pairLoading.rightKeywordProductivity,
    rightKeywordPopularity: pairLoading.rightKeywordPopularity
  } as const;

  const projectNameFromStore = useProjectFlowStore((state) => state.projectName);

  // Build dynamic version details using current/master keys or the selected version (mirroring useLibraryReview)
  const buildVersionDetails = useCallback(
    (selectionId: string, isLeft: boolean) => {
      if (selectionId === 'current') {
        return {
          source: 'Amazon',
          status: 'Live on retailer site',
          date: formatDateForDisplay(
            isLeft ? comparisonSections?.leftResolved?.timestamp : comparisonSections?.rightResolved?.timestamp
          )
        };
      }

      if (selectionId === 'master') {
        const formatted = formatDateForDisplay(
          uploadedOrMasterContent?.timestamp,
          uploadedOrMasterContent?.updatedAt
        );
        const uploadedLibraryStatus = uploadedOrMasterContent?.libraryStatus;
        const normalizedStatus = uploadedLibraryStatus
          ? `${uploadedLibraryStatus.slice(0, 1).toUpperCase()}${uploadedLibraryStatus.slice(1).toLowerCase()}`
          : null;
        return {
          source: 'Uploaded',
          status: normalizedStatus,
          ...(formatted ? { date: formatted } : {})
        };
      }

      const version = libraryRecord?.versions?.find((v) => v.skuContentGenerationId === selectionId);
      const normalizedStatus = version?.libraryStatus
        ? `${version.libraryStatus?.slice(0, 1).toUpperCase()}${version.libraryStatus?.slice(1).toLowerCase()}`
        : null;

      return {
        project: version?.projectName || projectNameFromStore || null,
        profile: version?.profileName || profile?.profileName || null,
        profileId: version?.profileId || profileIdInput || null,
        status: normalizedStatus
      };
    },
    [libraryRecord, comparisonSections, projectNameFromStore, profile?.profileName, profileIdInput, uploadedOrMasterContent]
  );

  const leftVersionDetails = useMemo(
    () => buildVersionDetails(leftSelectedVersion, true),
    [buildVersionDetails, leftSelectedVersion]
  );
  const rightVersionDetails = useMemo(
    () => buildVersionDetails(rightSelectedVersion, false),
    [buildVersionDetails, rightSelectedVersion]
  );

  // Legacy version details for backward compatibility
  const currentVersionDetails = leftVersionDetails;
  const generatedVersionDetails = rightVersionDetails;

  // Get display names
  const leftVersionName = useMemo(() => {
    const option = allVersionOptions.find((opt) => opt.id === leftSelectedVersion);
    return option?.label || 'Current Content';
  }, [leftSelectedVersion, allVersionOptions]);

  const rightVersionName = useMemo(() => {
    const option = allVersionOptions.find((opt) => opt.id === rightSelectedVersion);
    return option?.label || 'Current Content';
  }, [rightSelectedVersion, allVersionOptions]);

  // Handlers for version selection
  const handleLeftVersionChange = useCallback((newVersionId: string) => {
    setLeftSelectedVersion(newVersionId);
  }, []);

  const handleRightVersionChange = useCallback((newVersionId: string) => {
    setRightSelectedVersion(newVersionId);
  }, []);

  // Library actions
  const { updateContentLibrary: saveAsDraft, isPending: isSavingDraft } = useUpdateContentLibrary('draft');
  const { updateContentLibrary: saveAsMaster, isPending: isSavingMaster } = useUpdateContentLibrary('master');
  const { updateContentLibrary: archiveContent, isPending: isArchiving } = useUpdateContentLibrary('archive');
  const { exportContent, isPending: isExporting } = useExportContent();
  const isSaving = isSavingDraft || isSavingMaster || isArchiving;

  const hasGeneratedContent = !error && rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master';

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      goToPreviousContentGenItem();
    } else {
      clearContentGenItems();
      const generationsRoute = buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.GENERATION,
        subtab: BEACON_SUBTABS.GENERATE
      });
      beaconNavigator.go(generationsRoute);
    }
  }, [currentIndex, goToPreviousContentGenItem, clearContentGenItems, app, location, beaconNavigator]);

  const handleExport = useCallback(() => {
    const versionId =
      rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master' ? rightSelectedVersion : '';

    if (!versionId) {
      showSnackbar({ message: 'No generated content to export.', type: 'error' });
      return;
    }

    exportContent({
      retailerId: Number(retailerId),
      skuContentGenerationIds: [versionId]
    });
  }, [rightSelectedVersion, retailerId, exportContent, showSnackbar]);

  const handleArchive = hasGeneratedContent 
    ? () => archiveContent({ 
        skuContentGenerationId: rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master' ? rightSelectedVersion : (currentContentGenId || contentGenIdInput), 
        retailerId: Number(retailerId), 
        retailerSku: retailerSkuInput 
      }) 
    : undefined;

  const getSaveMenuOptions = () => [
    { id: 'saveToLibrary', label: 'Save to library' },
    { id: 'saveToMaster', label: 'Save as master' }
  ];

  const handleSaveOption = (option: { id: string; label: string }) => {
    const activeContentGenId = rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master' 
      ? rightSelectedVersion 
      : (currentContentGenId || contentGenIdInput);
    const right = comparisonSections?.rightResolved;
    const req: ContentLibraryManagementRequest = {
      skuContentGenerationId: activeContentGenId,
      retailerId: Number(retailerId),
      retailerSku: retailerSkuInput,
      title: right?.title ?? '',
      description: right?.description ?? '',
      bullets: Array.isArray(right?.bullets) ? right.bullets : [],
      itemHighlights: right?.itemHighlights
    };
    if (option.id === 'saveToLibrary') saveAsDraft(req);
    if (option.id === 'saveToMaster') saveAsMaster(req);
  };

  const handleNext = () => {
    if (currentIndex < totalReviews - 1) {
      goToNextContentGenItem();
    } else {
      showSnackbar({ message: 'All reviews completed!', type: 'success' });
      clearContentGenItems();
      const generationsRoute = buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.GENERATION,
        subtab: BEACON_SUBTABS.GENERATE
      });
      beaconNavigator.go(generationsRoute);
    }
  };

  const actionBarProps: ActionBarProps = {
    onBack: handleBack,
    canGoBack: currentIndex > 0,
    onNext: currentIndex < totalReviews - 1 ? handleNext : undefined,
    onArchive: handleArchive,
    onSave: hasGeneratedContent ? handleSaveOption : undefined,
    hasGeneratedContent,
    canGoNext: currentIndex < totalReviews - 1,
    isSaving,
    getSaveMenuOptions
  };

  return {
    // data
    profile,
    profileIdForDetails: profileIdInput,
    currentMetrics: leftMetrics,
    newMetrics: rightMetrics,
    // Expose as both left/right and current/new for flexibility
    leftMetrics,
    rightMetrics,
    metricLoadingStates,
    isSaving,
    isExporting,
    hasGeneratedContent,
    currentContentPhrasesKeywords,
    generatedContentPhrasesKeywords,
    // Expose as both naming conventions
    leftSections,
    rightSections,
    currentVersionDetails,
    generatedVersionDetails,
    leftVersionDetails,
    rightVersionDetails,
    currentContentGenId: rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master' 
      ? rightSelectedVersion 
      : (currentContentGenId || contentGenIdInput),
    projectReviewQueryKey,
    // Version selection
    availableVersions,
    leftComparisonOptions,
    leftSelectedVersion,
    rightSelectedVersion,
    leftVersionName,
    rightVersionName,
    // Syndication data
    retailerId: Number(retailerId),
    retailerSku: retailerSkuInput,
    // loading + error
    isLoading,
    isLoadingLibrary,
    isLoadingCompare,
    isLoadingLeft,
    isLoadingRight,
    error,
    // actions
    actionBarProps,
    onSetAsMaster: () => {
      const right = comparisonSections?.rightResolved;
      saveAsMaster({
        skuContentGenerationId: rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master'
          ? rightSelectedVersion
          : (currentContentGenId || contentGenIdInput),
        retailerId: Number(retailerId),
        retailerSku: retailerSkuInput,
        title: right?.title ?? '',
        description: right?.description ?? '',
        bullets: Array.isArray(right?.bullets) ? right.bullets : [],
        itemHighlights: right?.itemHighlights
      });
    },
    handleExport,
    handleLeftVersionChange,
    handleRightVersionChange
  };
};


